import React, { useState, useEffect } from 'react';
import api from '@/services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Webhook, Plus, Trash2, Loader2, TestTube, Copy, RefreshCw, AlertCircle, CheckCircle, Database } from 'lucide-react';
import { toast } from 'sonner';

const WEBHOOK_EVENTS = [
  { value: 'chat.session.started', label: 'New Conversation', desc: 'When a new chat session starts' },
  { value: 'chat.message.received', label: 'Message Received', desc: 'When user sends a message' },
  { value: 'chat.message.sent', label: 'Bot Response', desc: 'When bot sends a response' },
  { value: 'document.uploaded', label: 'Document Uploaded', desc: 'When a document is uploaded' },
  { value: 'document.processed', label: 'Document Processed', desc: 'When document processing completes' },
  { value: 'chatbot.activated', label: 'AI Employee Activated', desc: 'When chatbot is activated' },
  { value: 'chatbot.paused', label: 'AI Employee Paused', desc: 'When chatbot is paused' },
];

export const WebhooksTab = ({ chatbotId }) => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    events: ['chat.session.started', 'chat.message.received']
  });

  useEffect(() => {
    loadWebhooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbotId]);

  const loadWebhooks = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/chatbots/${chatbotId}/webhooks`);
      setWebhooks(response.data || []);
    } catch (error) {
      console.error('Failed to load webhooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWebhook = async () => {
    if (!newWebhook.name.trim() || !newWebhook.url.trim()) {
      toast.error('Name and URL are required');
      return;
    }

    if (!newWebhook.url.startsWith('http://') && !newWebhook.url.startsWith('https://')) {
      toast.error('URL must start with http:// or https://');
      return;
    }

    if (newWebhook.events.length === 0) {
      toast.error('Select at least one event');
      return;
    }

    setSavingWebhook(true);
    try {
      const response = await api.post(`/chatbots/${chatbotId}/webhooks`, newWebhook);
      setWebhooks(prev => [...prev, response.data]);
      setShowAddModal(false);
      setNewWebhook({ name: '', url: '', events: ['chat.session.started', 'chat.message.received'] });
      toast.success('Webhook created successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create webhook');
    } finally {
      setSavingWebhook(false);
    }
  };

  const handleToggleActive = async (webhookId, isActive) => {
    try {
      await api.patch(`/chatbots/${chatbotId}/webhooks/${webhookId}`, { is_active: !isActive });
      setWebhooks(prev => prev.map(w => w.webhook_id === webhookId ? { ...w, is_active: !isActive } : w));
      toast.success(isActive ? 'Webhook disabled' : 'Webhook enabled');
    } catch (error) {
      toast.error('Failed to update webhook');
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) return;

    try {
      await api.delete(`/chatbots/${chatbotId}/webhooks/${webhookId}`);
      setWebhooks(prev => prev.filter(w => w.webhook_id !== webhookId));
      toast.success('Webhook deleted');
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const handleTestWebhook = async (webhookId) => {
    setTestingId(webhookId);
    try {
      const response = await api.post(`/chatbots/${chatbotId}/webhooks/${webhookId}/test`);
      if (response.data.success) {
        toast.success(`Test successful! Status: ${response.data.status_code}`);
      } else {
        toast.error(`Test failed. Status: ${response.data.status_code || 'No response'}`);
      }
    } catch (error) {
      toast.error('Failed to send test webhook');
    } finally {
      setTestingId(null);
    }
  };

  const copySecret = (secret) => {
    navigator.clipboard.writeText(secret);
    toast.success('Secret copied to clipboard');
  };

  const toggleEvent = (event) => {
    setNewWebhook(prev => ({
      ...prev,
      events: prev.events.includes(event) 
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-slate-900 flex items-center gap-2">
                <Database className="w-5 h-5 text-indigo-600" />
                Webhooks & Integrations
              </CardTitle>
              <CardDescription className="text-slate-600">
                Connect your AI Employee to external systems, databases, and APIs for real-time data sync
              </CardDescription>
            </div>
            <Button onClick={() => setShowAddModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Webhook
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* How it works */}
      <Card className="bg-white border-slate-200">
        <CardContent className="pt-6">
          <h4 className="font-medium text-slate-900 mb-3">How Webhooks Work</h4>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="font-medium text-slate-700 mb-1">1. Configure Endpoint</div>
              <p className="text-slate-500">Add your server URL that will receive event data</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="font-medium text-slate-700 mb-1">2. Select Events</div>
              <p className="text-slate-500">Choose which AI Employee events trigger notifications</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="font-medium text-slate-700 mb-1">3. Process Data</div>
              <p className="text-slate-500">Your server receives JSON with event details and signatures</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Webhooks List */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-slate-900">Active Webhooks</CardTitle>
          <Button variant="outline" size="sm" onClick={loadWebhooks} disabled={loading} className="border-slate-300">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </CardHeader>
        <CardContent>
          {loading && webhooks.length === 0 ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          ) : webhooks.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-slate-300 rounded-lg">
              <Webhook className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600 mb-2">No webhooks configured</p>
              <p className="text-sm text-slate-500 mb-4">Add webhooks to sync AI Employee events with your systems</p>
              <Button onClick={() => setShowAddModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Add First Webhook
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {webhooks.map((webhook) => (
                <div key={webhook.webhook_id} className="p-4 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-slate-900">{webhook.name}</h4>
                        <Badge className={webhook.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}>
                          {webhook.is_active ? 'Active' : 'Disabled'}
                        </Badge>
                        {webhook.failure_count > 0 && (
                          <Badge className="bg-amber-100 text-amber-700">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {webhook.failure_count} failures
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 font-mono truncate">{webhook.url}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {webhook.events?.map((event) => (
                          <Badge key={event} variant="outline" className="text-xs border-indigo-200 text-indigo-700">
                            {event}
                          </Badge>
                        ))}
                      </div>
                      {webhook.last_triggered_at && (
                        <p className="text-xs text-slate-400 mt-2">
                          Last triggered: {new Date(webhook.last_triggered_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={webhook.is_active}
                        onCheckedChange={() => handleToggleActive(webhook.webhook_id, webhook.is_active)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copySecret(webhook.secret)}
                        className="text-slate-500 hover:text-slate-700"
                        title="Copy secret"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTestWebhook(webhook.webhook_id)}
                        disabled={testingId === webhook.webhook_id}
                        className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                        title="Test webhook"
                      >
                        {testingId === webhook.webhook_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <TestTube className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteWebhook(webhook.webhook_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete webhook"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Signature Verification Info */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-6">
          <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            Signature Verification
          </h4>
          <p className="text-sm text-slate-600 mb-3">
            All webhook requests include an HMAC-SHA256 signature for verification. Check the <code className="bg-white px-1 rounded">X-Harmonix-Signature</code> header.
          </p>
          <pre className="bg-white p-3 rounded text-xs overflow-x-auto border border-slate-200">
{`// Example verification (Node.js)
const crypto = require('crypto');

function verifySignature(payload, signature, secret) {
  const expected = 'sha256=' + crypto
    .createHmac('sha256', secret)
    .update(JSON.stringify(payload))
    .digest('hex');
  return signature === expected;
}`}
          </pre>
        </CardContent>
      </Card>

      {/* Add Webhook Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Add Webhook</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-600">Webhook Name</Label>
              <Input
                value={newWebhook.name}
                onChange={(e) => setNewWebhook({ ...newWebhook, name: e.target.value })}
                placeholder="e.g., CRM Integration, Database Sync"
                className="bg-white border-slate-300 mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-600">Endpoint URL</Label>
              <Input
                value={newWebhook.url}
                onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                placeholder="https://your-server.com/webhook"
                className="bg-white border-slate-300 mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Must be HTTPS for production use</p>
            </div>
            <div>
              <Label className="text-slate-600 mb-2 block">Events to Subscribe</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {WEBHOOK_EVENTS.map((event) => (
                  <div
                    key={event.value}
                    className={`flex items-start gap-3 p-2 rounded cursor-pointer transition-colors ${
                      newWebhook.events.includes(event.value) ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50'
                    }`}
                    onClick={() => toggleEvent(event.value)}
                  >
                    <Checkbox
                      checked={newWebhook.events.includes(event.value)}
                      onCheckedChange={() => toggleEvent(event.value)}
                      className="mt-0.5"
                    />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{event.label}</p>
                      <p className="text-xs text-slate-500">{event.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} className="border-slate-300">
              Cancel
            </Button>
            <Button
              onClick={handleCreateWebhook}
              disabled={savingWebhook}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {savingWebhook && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WebhooksTab;
